'use strict'

const PUBLIC_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const CACHE_PREFIX = 'preika.route-reference'

let activeAccountId = null
const memoryCache = new Map()

export function isPublicRouteId (value) {
  return PUBLIC_ID_PATTERN.test(String(value == null ? '' : value).trim())
}

export function isNumericRouteId (value) {
  const text = String(value == null ? '' : value).trim()
  if (!/^\d+$/.test(text)) return false
  const id = Number(text)
  return Number.isSafeInteger(id) && id > 0
}

/** ログイン企業が変わった場合に前企業の解決結果を再利用しない。 */
export function setRouteReferenceAccount (accountId) {
  const normalized = Number(accountId)
  activeAccountId = Number.isSafeInteger(normalized) && normalized > 0
    ? normalized
    : null
}

export function clearRouteReferenceCache () {
  memoryCache.clear()
  try {
    const prefix = activeAccountId == null
      ? `${CACHE_PREFIX}.`
      : `${CACHE_PREFIX}.${activeAccountId}.`
    const keys = []
    for (let index = 0; index < sessionStorage.length; index++) {
      const key = sessionStorage.key(index)
      if (key && key.startsWith(prefix)) keys.push(key)
    }
    keys.forEach(key => sessionStorage.removeItem(key))
  } catch (_) {
    // storage利用不可でもmemoryCacheは消去済み。
  }
}

function cacheKey (publicId) {
  const account = activeAccountId == null ? 'unknown' : String(activeAccountId)
  return `${CACHE_PREFIX}.${account}.${String(publicId).toLowerCase()}`
}

function cachedValue (publicId) {
  const normalizedPublicId = String(publicId == null ? '' : publicId).trim().toLowerCase()
  if (!isPublicRouteId(normalizedPublicId)) return null
  const key = cacheKey(normalizedPublicId)

  let value = memoryCache.get(key)
  if (!value) {
    try {
      const stored = sessionStorage.getItem(key)
      value = stored ? JSON.parse(stored) : null
    } catch (_) {
      value = null
    }
    if (value) memoryCache.set(key, value)
  }
  return value
}

export function cacheResolvedRouteReference (
  publicId,
  internalId,
  resourceType = '',
  displayLabel = ''
) {
  const normalizedPublicId = String(publicId == null ? '' : publicId).trim().toLowerCase()
  const normalizedInternalId = Number(internalId)
  if (!isPublicRouteId(normalizedPublicId) ||
      !Number.isSafeInteger(normalizedInternalId) || normalizedInternalId <= 0) {
    throw new Error('公開参照IDと内部IDの対応が正しくありません。')
  }

  const value = {
    internalId: normalizedInternalId,
    resourceType: String(resourceType || '').trim().toUpperCase(),
    displayLabel: String(displayLabel || '').trim()
  }
  const key = cacheKey(normalizedPublicId)
  memoryCache.set(key, value)
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch (_) {
    // sessionStorageが利用できない環境でも、同一画面内はmemoryCacheで継続する。
  }
  return normalizedInternalId
}

export function cachedInternalRouteId (publicId, resourceType = '') {
  const expectedType = String(resourceType || '').trim().toUpperCase()
  const value = cachedValue(publicId)
  const internalId = Number(value && value.internalId)
  if (!Number.isSafeInteger(internalId) || internalId <= 0) return null
  if (expectedType && String(value.resourceType || '').toUpperCase() !== expectedType) {
    return null
  }
  return internalId
}

export function cachedRouteReferenceLabel (publicId, resourceType = '') {
  const expectedType = String(resourceType || '').trim().toUpperCase()
  const value = cachedValue(publicId)
  if (!value) return ''
  if (expectedType && String(value.resourceType || '').toUpperCase() !== expectedType) {
    return ''
  }
  return String(value.displayLabel || '').trim()
}

export function resolveRouteValue (route, key = 'id', resourceType = '') {
  const value = route || {}
  const params = value.params || {}
  const query = value.query || {}
  const candidates = [params[key], query[key]]

  for (const candidate of candidates) {
    if (isNumericRouteId(candidate)) return Number(candidate)
    if (isPublicRouteId(candidate)) {
      const resolved = cachedInternalRouteId(candidate, resourceType)
      if (resolved != null) return resolved
    }
  }
  return null
}

/** Vue Routerのpath/queryから、数値または解決済みUUIDの内部IDを取得する。 */
export function resolveRouteRecordId (route, resourceType = '') {
  return resolveRouteValue(route, 'id', resourceType)
}

/** URLへ保持できるquery形式のレコード遷移先を作る。数値はGuardがUUIDへ置換する。 */
export function recordQueryRoute (name, id) {
  const text = String(id == null ? '' : id).trim()
  if (!name || (!isNumericRouteId(text) && !isPublicRouteId(text))) {
    throw new Error('有効な画面名とレコードIDを指定してください。')
  }
  return {
    name,
    query: { id: isPublicRouteId(text) ? text.toLowerCase() : String(Number(text)) }
  }
}
